import React, { useState, useEffect, useCallback, useRef } from 'react';
import '@/App.css';
import axios from 'axios';

// Global axios interceptor to add session token to all requests
axios.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('session_token');
  if (token) {
    config.headers['X-Session-Token'] = token;
  }
  return config;
});
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PharmacyProvider } from './context/PharmacyContext';
import PharmacySelector from './components/PharmacySelector';
import Profile from './pages/Profile';
import Dashboard from './pages/Dashboard';
import Revenues from './pages/Revenues';
import Expenses from './pages/Expenses';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Login from './pages/Login';
import RegisterPharmacy from './pages/RegisterPharmacy';
import RegisterEmployee from './pages/RegisterEmployee';
import Users from './pages/UsersManagement';
import CategoriesManagement from './pages/CategoriesManagement';
import FournisseursV2 from './pages/FournisseursV2';
import SecuriteSociale from './pages/SecuriteSociale';
import PharmaciesManagement from './pages/PharmaciesManagement';
import PlatformAdmin from './pages/PlatformAdmin';
import TechnicalSettings from './pages/TechnicalSettings';
import LandingPage from './pages/LandingPage';
import PnL from './pages/PnL';
import PharmacyRequests from './pages/PharmacyRequests';
import GestionCaisses from './pages/GestionCaisses';
import Caisse from './pages/Caisse';
import Tresorerie from './pages/Tresorerie';
import TresorerieEcheances from './pages/TresorerieEcheances';
import FacturesBL from './pages/FacturesBL';
import Banque from './pages/Banque';
import Employees from './pages/Employees';
import EmployeeDetails from './pages/Employees/EmployeeDetails';
import Cheques from './pages/Cheques';
import {
  LayoutDashboard,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Calendar,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  Users as UsersIconMenu,
  Building,
  Building2,
  FileText,
  UserCheck,
  Wallet,
  User,
  Eye,
  EyeOff,
  CreditCard
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function AppContent() {
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(() => {
    // Initialiser currentPage en fonction du rôle utilisateur stocké
    try {
      const storedUser = sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        return userData.role === 'caissier' ? 'gestion-caisses' : 'dashboard';
      }
    } catch (error) {
      console.error('Failed to parse user from localStorage:', error);
    }
    return 'dashboard';
  });
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLanding, setShowLanding] = useState(false); // false = login direct, true = landing page
  const [showRegister, setShowRegister] = useState(false);
  const [showRegisterEmployee, setShowRegisterEmployee] = useState(false);
  const [colorVersion] = useState(1); // 1=Bleu/Cyan (QuickBooks style), 2=Vert/Émeraude (Pharmacie)

  useEffect(() => {
    // Check for stored user + valid session
    const storedUser = sessionStorage.getItem('pharmacy_user');
    const sessionToken = sessionStorage.getItem('session_token');
    if (storedUser && sessionToken) {
      const userData = JSON.parse(storedUser);
      // Validate session with backend
      axios.post(`${API}/auth/validate-session`, {}, {
        headers: { 'X-User-Id': userData.id, 'X-Session-Token': sessionToken }
      }).then((res) => {
        const freshUserData = res.data.user;
        if (freshUserData) {
          freshUserData.session_token = sessionToken;
          setUser(freshUserData);
          sessionStorage.setItem('pharmacy_user', JSON.stringify(freshUserData));
        } else {
          setUser(userData);
        }
        setShowLanding(false);
      }).catch(() => {
        // Session expired or invalid
        sessionStorage.removeItem('pharmacy_user');
        sessionStorage.removeItem('session_token');
        setShowLanding(true);
      });
    } else {
      // Also check localStorage for migration (old sessions)
      const oldUser = localStorage.getItem('pharmacy_user');
      if (oldUser) {
        localStorage.removeItem('pharmacy_user');
        // Old sessions without token are invalid — force re-login
      }
    }

  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setShowLanding(false);
    setShowRegister(false);
    // Store in sessionStorage (cleared on browser/tab close)
    sessionStorage.setItem('pharmacy_user', JSON.stringify(userData));
    if (userData.session_token) {
      sessionStorage.setItem('session_token', userData.session_token);
    }
    localStorage.removeItem('pharmacy_user'); // Clean old storage
    
    // Rediriger automatiquement les caissiers vers Clôture Caisse
    if (userData.role === 'caissier') {
      setCurrentPage('gestion-caisses');
    } else {
      setCurrentPage('dashboard');
    }
    
    toast.success('Connexion réussie!');
  };

  const handleLogout = async () => {
    try {
      if (user) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: {
            'X-User-Id': user.id,
            'X-Session-Token': user.session_token
          }
        });
      }
    } catch (e) {
      console.error('Logout error', e);
    }
    setUser(null);
    sessionStorage.removeItem('pharmacy_user');
    sessionStorage.removeItem('session_token');
    localStorage.removeItem('pharmacy_user');
    // We do NOT clear localStorage 'token' if it's not used, but let's clear it just in case
    localStorage.removeItem('token'); 
  };

  // ============ SECURITY: Auto-logout system ============
  const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutes
  const logoutTriggered = useRef(false);

  // Record activity timestamp
  const recordActivity = useCallback(() => {
    if (user) localStorage.setItem('last_activity', String(Date.now()));
  }, [user]);

  useEffect(() => {
    if (!user) {
      logoutTriggered.current = false;
      return;
    }
    recordActivity();

    // Check every 30 seconds if session expired
    const interval = setInterval(() => {
      const last = parseInt(localStorage.getItem('last_activity') || '0', 10);
      if (last > 0 && Date.now() - last > INACTIVITY_LIMIT && !logoutTriggered.current) {
        logoutTriggered.current = true;
        handleLogout();
        toast.warning('Session expirée après 15 minutes d\'inactivité');
      }
    }, 30000);

    // Listen for user activity
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    const handler = () => recordActivity();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));

    // Check on tab re-focus
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        const last = parseInt(localStorage.getItem('last_activity') || '0', 10);
        if (last > 0 && Date.now() - last > INACTIVITY_LIMIT && !logoutTriggered.current) {
          logoutTriggered.current = true;
          handleLogout();
          toast.warning('Session expirée après 30 minutes d\'inactivité');
        }
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);

    // Removed beforeunload logout to prevent logout on page refresh

    // Axios interceptor: auto-logout on 401
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && user && !logoutTriggered.current) {
          logoutTriggered.current = true;
          handleLogout();
          toast.warning('Session expirée. Veuillez vous reconnecter.');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      clearInterval(interval);
      events.forEach(e => window.removeEventListener(e, handler));
      document.removeEventListener('visibilitychange', visibilityHandler);
      axios.interceptors.response.eject(interceptorId);
    };
  }, [user, recordActivity, handleLogout]);

  useEffect(() => {
    const handleNavigate = (e) => {
      if (e.detail) {
        setCurrentPage(e.detail);
      }
    };
    window.addEventListener('navigate', handleNavigate);
    return () => window.removeEventListener('navigate', handleNavigate);
  }, []);

  // Show register employee page
  if (showRegisterEmployee) {
    return (
      <>
        <RegisterEmployee 
          onBackToHome={() => {
            setShowRegisterEmployee(false);
            setShowLanding(true);
          }}
          onNavigateToLogin={() => {
            setShowRegisterEmployee(false);
            setShowLanding(false);
          }}
        />
        <Toaster position="top-right" />
      </>
    );
  }

  // Show register page
  if (showRegister) {
    return (
      <>
        <RegisterPharmacy 
          onBackToHome={() => {
            setShowRegister(false);
            setShowLanding(true);
          }}
          onNavigateToLogin={() => {
            setShowRegister(false);
            setShowLanding(false);
          }}
        />
        <Toaster position="top-right" />
      </>
    );
  }

  // Show landing page if not logged in
  if (showLanding && !user) {
    return (
      <>
        <LandingPage 
          colorVersion={colorVersion} 
          onNavigateToLogin={() => setShowLanding(false)}
          onNavigateToRegister={() => {
            setShowLanding(false);
            setShowRegister(true);
          }}
          onNavigateToRegisterEmployee={() => {
            setShowLanding(false);
            setShowRegisterEmployee(true);
          }}
        />
        <Toaster position="top-right" />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Login onLogin={handleLogin} />
        <Toaster position="top-right" />
      </>
    );
  }

  // Helper function to check if user is pharmacy admin (owner or admin)
  const isPharmacyAdmin = (user) => {
    return user?.role === 'super_admin' || user?.role === 'owner' || user?.role === 'admin';
  };

  const isManager = (user) => {
    return isPharmacyAdmin(user) || user?.role === 'manager';
  };

  // Helper function to check if user has ANY permission for a module
  const hasAnyPermissionForModule = (module) => {
    if (!user) return false;
    
    // Super admin always has access
    if (user.role === 'super_admin') return true;
    
    const permissions = user.granular_permissions || {};
    const modulePerms = permissions[module];
    
    if (!modulePerms) return false;
    
    // Recursive check for nested permissions
    const checkPerms = (obj) => {
      if (!obj) return false;
      if (typeof obj === 'object') {
        // Check current level
        for (const [k, v] of Object.entries(obj)) {
           if (v === true) return true;
        }
        
        // Recursively check deeper levels
        for (const [k, v] of Object.entries(obj)) {
           if (typeof v === 'object' && v !== null) {
               if (checkPerms(v)) return true;
           }
        }
        return false;
      }
      return false;
    };
    
    return checkPerms(modulePerms);
  };



  // Filter menu items based on user role and permissions
  const allMenuItems = [
    // Super Admin - Full access to everything
    { id: 'platform-admin', label: 'Admin Plateforme', icon: Building2, visible: user?.role === 'super_admin' },
    { id: 'pharmacy-requests', label: 'Demandes Inscription', icon: UserCheck, visible: user?.role === 'super_admin' },
    { id: 'technical-settings', label: 'Paramètres Techniques', icon: SettingsIcon, visible: user?.role === 'super_admin' },
    
    // Pharmacy Admin and Employees - Access based on granular permissions
    { id: 'caisse', label: 'Cash', icon: Wallet, visible: hasAnyPermissionForModule('caisse') || hasAnyPermissionForModule('closures') },
    { id: 'factures-bl', label: 'Factures & BL', icon: FileText, visible: hasAnyPermissionForModule('factures_bl') },
    { id: 'securite-sociale', label: 'Sécurité Sociale', icon: Building2, visible: hasAnyPermissionForModule('securite_sociale') },
    { id: 'banque', label: 'Banque', icon: Building, visible: hasAnyPermissionForModule('banque') },
    { id: 'cheques', label: 'Chèques', icon: CreditCard, visible: hasAnyPermissionForModule('banque') },
    { id: 'revenues', label: 'Revenus', icon: TrendingUp, visible: hasAnyPermissionForModule('revenues') },
    { id: 'pnl', label: 'PnL', icon: FileText, visible: hasAnyPermissionForModule('pnl') },
    { id: 'suppliers', label: 'Fournisseurs', icon: Building, visible: hasAnyPermissionForModule('suppliers') },
    { id: 'analytics', label: 'Analyses', icon: BarChart3, visible: hasAnyPermissionForModule('analytics') },
    { id: 'tresorerie-echeances', label: 'Trésorerie & Échéances', icon: Calendar, visible: isManager(user) || hasAnyPermissionForModule('analytics') },
    
    // GESTION section
    { id: 'employes', label: 'Employés', icon: UsersIconMenu, visible: hasAnyPermissionForModule('employees') || isPharmacyAdmin(user) },
    { id: 'users', label: 'Utilisateurs', icon: UsersIconMenu, visible: hasAnyPermissionForModule('users') || isPharmacyAdmin(user) },
    { id: 'settings', label: 'Paramètres', icon: SettingsIcon, visible: isPharmacyAdmin(user) },
  ];

  const menuItems = allMenuItems.filter(item => item.visible);

  const renderPage = () => {
    // Super admin has full access to all pages
    switch (currentPage) {
      case 'platform-admin':
        return <PlatformAdmin currentUser={user} />;
      case 'pharmacy-requests':
        return <PharmacyRequests currentUser={user} />;
      case 'technical-settings':
        return <TechnicalSettings currentUser={user} />;
      case 'dashboard':
        if (isManager(user)) {
          return <Dashboard />;
        }
        return <Revenues />; // Redirect to revenues for regular users
      case 'users':
        if (hasAnyPermissionForModule('users') || isPharmacyAdmin(user)) {
          return <Users currentUser={user} />;
        }
        return <Revenues />;
      case 'employes':
        return <Employees />;
      case 'employee-details':
        return <EmployeeDetails />;
      case 'pharmacies':
        if (hasAnyPermissionForModule('pharmacies') || isPharmacyAdmin(user)) {
          return <PharmaciesManagement currentUser={user} />;
        }
        return <Revenues />;
      case 'revenues':
        return <Revenues />;
      case 'expenses':
        return <Expenses />;
      case 'tresorerie':
        return <Tresorerie />;
      case 'tresorerie-echeances':
        return <TresorerieEcheances />;
      case 'banque':
        return <Banque />;
      case 'cheques':
        return <Cheques />;
      case 'caisse':
        return <Caisse />;
      case 'gestion-caisses':
        return <GestionCaisses />;
      case 'securite-sociale':
        return <SecuriteSociale />;
      case 'pnl':
        if (hasAnyPermissionForModule('pnl')) {
          return <PnL />;
        }
        return <Revenues />;
      case 'suppliers':
        return <FournisseursV2 currentUser={user} />;
      case 'factures-bl':
        return <FacturesBL />;
      case 'analytics':
        if (hasAnyPermissionForModule('analytics')) {
          return <Analytics />;
        }
        return <Revenues />;
      case 'settings':
        if (user?.role === 'super_admin') {
          return <TechnicalSettings currentUser={user} />;
        }
        return <CategoriesManagement currentUser={user} />;
      case 'profile':
        return <Profile />;
      default:
        if (user?.role === 'super_admin') {
          return <PlatformAdmin currentUser={user} />;
        }
        if (user?.role === 'admin' || user?.role === 'manager') {
          return <Dashboard />;
        }
        return <Revenues />;
    }
  };

  return (
    <div className="flex h-screen overflow-hidden w-full bg-gray-50">
      {/* Sidebar - NOUVEAU DESIGN BLANC */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-white border-r border-gray-200 text-gray-700 transition-all duration-300 ease-in-out hidden lg:flex flex-col shadow-sm`}
      >
        <div className="p-6 flex items-center justify-between border-b border-gray-100">
          {sidebarOpen && (
            <div>
              <h1 className="text-2xl font-bold text-gray-900">MIZAN</h1>
              <p className="text-xs text-gray-500 mt-1">Accounting - v2.4</p>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="sidebar-toggle-btn"
          >
            {sidebarOpen ? <X size={20} className="text-gray-600" /> : <Menu size={20} className="text-gray-600" />}
          </button>
        </div>

        {user?.role !== 'super_admin' && (
          <div className={`px-4 py-4 border-b border-gray-100 flex items-center justify-center ${!sidebarOpen ? 'hidden' : ''}`}>
            <PharmacySelector />
          </div>
        )}

        {/* Section PRINCIPAL */}
        {sidebarOpen && (
          <div className="px-4 pt-4 pb-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">PRINCIPAL</p>
          </div>
        )}

        <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id)}
                data-testid={`nav-${item.id}`}
                className={`w-full flex items-center ${
                  sidebarOpen ? 'px-4' : 'px-3 justify-center'
                } py-3 rounded-lg transition-all ${
                  currentPage === item.id
                    ? 'bg-emerald-50 text-emerald-600 font-medium'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <Icon size={20} className="flex-shrink-0" />
                {sidebarOpen && <span className="ml-3 truncate">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-100 space-y-2">
          {user?.role && !['super_admin', 'admin', 'owner', 'gerant'].includes(user.role) && (
            <button
              onClick={() => setCurrentPage('employee-details')}
              className={`w-full flex items-center ${
                sidebarOpen ? 'px-4' : 'px-3 justify-center'
              } py-3 rounded-lg hover:bg-violet-50 transition-colors text-gray-700 hover:text-violet-600`}
            >
              <FileText size={20} className="flex-shrink-0" />
              {sidebarOpen && <span className="ml-3 font-medium truncate">Ma Fiche RH</span>}
            </button>
          )}
          <button
            onClick={() => setCurrentPage('profile')}
            className={`w-full flex items-center ${
              sidebarOpen ? 'px-4' : 'px-3 justify-center'
            } py-3 rounded-lg hover:bg-gray-100 transition-colors text-gray-700`}
          >
            <User size={20} className="flex-shrink-0" />
            {sidebarOpen && <span className="ml-3 font-medium truncate">Mon Profil</span>}
          </button>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className={`w-full flex items-center ${
              sidebarOpen ? 'px-4' : 'px-3 justify-center'
            } py-3 rounded-lg hover:bg-red-50 transition-colors text-gray-700 hover:text-red-600`}
          >
            <LogOut size={20} className="flex-shrink-0" />
            {sidebarOpen && <span className="ml-3 font-medium truncate">Déconnexion</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileMenuOpen(false)}>
          <aside 
            className="w-72 bg-gradient-to-br from-cyan-600 to-blue-700 text-white h-full flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header fixe */}
            <div className="p-5 flex items-center justify-between flex-shrink-0">
              <div>
                <h1 className="text-2xl font-bold">MIZAN</h1>
                <p className="text-xs text-cyan-100 mt-0.5">Accounting</p>
              </div>
              <button 
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Nav scrollable */}
            <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1" style={{ WebkitOverflowScrolling: 'touch' }}>
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setCurrentPage(item.id);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-3 rounded-xl transition-all text-sm ${
                      currentPage === item.id
                        ? 'bg-white text-cyan-600 shadow-lg font-semibold'
                        : 'hover:bg-white/10 text-white'
                    }`}
                  >
                    <Icon size={18} />
                    <span className="ml-3 font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Déconnexion fixe en bas */}
            <div className="p-3 border-t border-white/10 flex-shrink-0 space-y-2" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
              {user?.role && !['super_admin', 'admin', 'owner', 'gerant'].includes(user.role) && (
                <button
                  onClick={() => { setCurrentPage('employee-details'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center px-4 py-3 rounded-xl hover:bg-white/10 transition-colors text-white"
                >
                  <FileText size={18} />
                  <span className="ml-3 font-medium">Ma Fiche RH</span>
                </button>
              )}
              <button
                onClick={() => { setCurrentPage('profile'); setMobileMenuOpen(false); }}
                className="w-full flex items-center px-4 py-3 rounded-xl hover:bg-white/10 transition-colors text-white"
              >
                <User size={18} />
                <span className="ml-3 font-medium">Mon Profil</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-3 rounded-xl hover:bg-red-500/20 transition-colors text-red-100"
              >
                <LogOut size={18} />
                <span className="ml-3 font-medium">Déconnexion</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto relative">
        {/* Top Bar (Mobile only) */}
        <header className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-lg"
              data-testid="mobile-menu-btn"
            >
              <Menu size={24} />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {menuItems.find((item) => item.id === currentPage)?.label}
              </h2>
              <p className="text-sm text-gray-500">Bienvenue, {user.username}</p>
            </div>
            
            {/* Pharmacy Selector - Only for non-super admin */}
            {user.role !== 'super_admin' && (
              <div className="ml-4">
                <PharmacySelector />
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-700">{user.username}</p>
              <p className="text-xs text-gray-500 capitalize">{user.role}</p>
            </div>
            <button 
              onClick={() => setCurrentPage('profile')}
              className="relative w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold hover:ring-2 hover:ring-cyan-500 hover:ring-offset-2 transition-all cursor-pointer overflow-hidden"
            >
              {user.avatar_url ? (
                <img src={`${BACKEND_URL}${user.avatar_url}`} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                user.username.charAt(0).toUpperCase()
              )}
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">{renderPage()}</div>
      </main>

      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <PharmacyProvider>
      <AppContent />
    </PharmacyProvider>
  );
}

export default App;
